import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, Calendar, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventRegister() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState({ name: '', gender: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/events/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setEvent(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/events/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.status === 409) {
        alert(data.detail || 'You have already registered previously.');
      } else if (data.success) {
        setSuccess(true);
        setSuccessMsg(data.message);
      }
    } catch {}
    setSubmitting(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><p className="text-white">Loading...</p></div>;
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Event Not Found</h1>
          <p className="text-gray-400">This event link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#F5A623]" />
            </div>
          </div>
          <h1 className="text-4xl font-black text-white mb-4">You're Registered!</h1>
          <p className="text-gray-300 text-lg mb-2">{successMsg}</p>
          <p className="text-gray-400 mt-4">A confirmation email has been sent to your inbox.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="bg-[#1A1A1D] border border-white/10 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#F5A623] to-[#E59400] p-6 text-center">
            <h1 className="text-2xl sm:text-3xl font-black text-black mb-2">{event.name}</h1>
            {event.description && <p className="text-black/70 text-sm">{event.description}</p>}
          </div>

          {/* Event Details */}
          <div className="flex justify-center gap-6 py-4 border-b border-white/10">
            {event.date && (
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar className="w-4 h-4 text-[#F5A623]" />
                <span className="text-sm">{event.date}</span>
              </div>
            )}
            {event.time && (
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="w-4 h-4 text-[#F5A623]" />
                <span className="text-sm">{event.time}</span>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-gray-300">Full Name *</Label>
              <Input
                data-testid="event-name-input"
                name="name" required value={formData.name} onChange={handleChange}
                className="bg-[#0A0A0B] border-white/20 text-white h-11"
                placeholder="Enter your full name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Gender *</Label>
              <select
                data-testid="event-gender-input"
                name="gender" required value={formData.gender} onChange={handleChange}
                className="w-full bg-[#0A0A0B] border border-white/20 text-white rounded-md px-3 h-11 focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50"
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Mobile Number *</Label>
              <Input
                data-testid="event-phone-input"
                name="phone" type="tel" required value={formData.phone} onChange={handleChange}
                className="bg-[#0A0A0B] border-white/20 text-white h-11"
                placeholder="+60 12 345 6789"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Email Address *</Label>
              <Input
                data-testid="event-email-input"
                name="email" type="email" required value={formData.email} onChange={handleChange}
                className="bg-[#0A0A0B] border-white/20 text-white h-11"
                placeholder="your.email@example.com"
              />
            </div>
            <Button
              data-testid="event-register-btn"
              type="submit" disabled={submitting}
              className="w-full bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-12 text-lg"
            >
              {submitting ? 'Registering...' : 'Join Free Webinar Now'}
            </Button>
            <p className="text-center text-xs text-gray-500">Your information is secure and will only be used for this event.</p>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-6">Signature of Wealth by Steven Yong</p>
      </div>
    </div>
  );
}
